import { useState } from 'preact/hooks';
import { api } from '../../lib/api.js';
import { ticketNumber } from './maintenance-shared.js';

/**
 * Inline editor for a maintenance issue or event ticket number. Read-only
 * display by default; an admin sees a tiny pencil button that swaps in an
 * input + save/cancel.
 *
 * Props:
 *   row       — the issue or event object (must have `id` and `ticketNumber`)
 *   resource  — 'issues' | 'events' — used to build the API path
 *   isAdmin   — only show the edit affordance when true
 *   onSaved   — called after a successful save; parent should refetch
 */
export const TicketNumberEditor = ({ row, resource, isAdmin, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(row.ticketNumber ?? '');
  const [busy, setBusy] = useState(false);

  const current = ticketNumber(row);

  const start = () => {
    setValue(row.ticketNumber ?? current);
    setEditing(true);
  };

  const cancel = () => {
    setValue(row.ticketNumber ?? '');
    setEditing(false);
  };

  const save = async () => {
    const next = value.trim();
    if (!next) return;
    if (next === row.ticketNumber) { setEditing(false); return; }
    setBusy(true);
    try {
      await api.patch(`/maintenance/${resource}/${row.id}/ticket-number`, {
        ticketNumber: next,
      });
      setEditing(false);
      onSaved?.();
    } catch (err) {
      alert(err.message ?? 'Failed to save ticket number');
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <span class="inline-flex items-center gap-1.5">
        <span>{current}</span>
        {isAdmin && (
          <button
            type="button"
            onClick={start}
            title="Edit ticket number"
            class="rounded border border-transparent px-1 text-text-muted hover:border-border-strong hover:text-text-body focus:outline-none focus-visible:ring-1 focus-visible:ring-brand"
          >
            ✎
          </button>
        )}
      </span>
    );
  }

  return (
    <span class="inline-flex items-center gap-1.5">
      <input
        type="text"
        value={value}
        onInput={(e) => setValue(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') cancel();
        }}
        disabled={busy}
        autoFocus
        maxLength={32}
        class="w-24 rounded border border-border-strong bg-bg-base px-1.5 py-0.5 font-mono text-[11px] font-semibold text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
      />
      <button
        type="button"
        onClick={save}
        disabled={busy}
        title="Save"
        class="rounded border border-border-strong px-1 text-status-ok hover:bg-bg-base disabled:opacity-40"
      >
        ✓
      </button>
      <button
        type="button"
        onClick={cancel}
        disabled={busy}
        title="Cancel"
        class="rounded border border-border-strong px-1 text-text-muted hover:bg-bg-base disabled:opacity-40"
      >
        ✕
      </button>
    </span>
  );
};
